import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { CardData } from '../game/types';
import { validateDeckComposition } from '../game/deck';
import { LAYOUT_BLOCK_SIZE, suggestLayout } from '../game/deckLayout';
import { Card } from './Board';

import { SERVER_ORIGIN } from '../config';

// Ogni mazzo della run è disposto a blocchi (vedi game/deckLayout.ts):
// tavolo / mano 1 / mano 2 da 4 Hand card + le 2 Mission. L'ordine salvato
// nello snapshot È la disposizione. Il loot della run è una lista libera.
type DeckKey = 'A' | 'B';
type BlockKey = 'table' | 'hand1' | 'hand2' | 'missions';
const HAND_BLOCKS: BlockKey[] = ['table', 'hand1', 'hand2'];
const ALL_BLOCKS: BlockKey[] = [...HAND_BLOCKS, 'missions'];
const BLOCK_LABEL: Record<BlockKey, string> = { table: 'Table', hand1: 'Hand 1', hand2: 'Hand 2', missions: 'Missions' };
const BLOCK_MAX: Record<BlockKey, number> = { table: LAYOUT_BLOCK_SIZE, hand1: LAYOUT_BLOCK_SIZE, hand2: LAYOUT_BLOCK_SIZE, missions: 2 };

type DeckBlocks = Record<BlockKey, CardData[]>;
// Zona = un blocco di un mazzo, oppure il loot.
type ZoneRef = { deck: DeckKey; block: BlockKey } | { deck: 'loot' };

// Da mazzo ordinato a blocchi. Un mazzo che ha perso carte (snapshot
// invalidato) ha meno di 12 Hand card: i buchi finiscono in coda a mano 2.
function toBlocks(cards: CardData[]): DeckBlocks {
  const hand = cards.filter(c => c.type === 'Hand');
  return {
    table: hand.slice(0, 4),
    hand1: hand.slice(4, 8),
    hand2: hand.slice(8, 12),
    missions: cards.filter(c => c.type === 'Mission'),
  };
}

function fromBlocks(b: DeckBlocks): CardData[] {
  return [...b.table, ...b.hand1, ...b.hand2, ...b.missions];
}

interface ReconfigureOverlayProps {
  open: boolean;
  initialDeckA: CardData[];
  initialDeckB: CardData[];
  initialLoot: CardData[];
  token: string;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSaved: (updatedRun: any) => void;
}

// Il puntatore decide il bersaglio; una carta sotto il puntatore vince sulla
// zona che la contiene (drop su carta = scambio di posto).
const collisionDetection: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  const slot = hits.find(h => String(h.id).startsWith('card:'));
  if (slot) return [slot];
  return hits.length > 0 ? hits : rectIntersection(args);
};

export function ReconfigureOverlay({
  open, initialDeckA, initialDeckB, initialLoot, token, onClose, onSaved,
}: ReconfigureOverlayProps) {
  const [decks, setDecks] = useState<Record<DeckKey, DeckBlocks>>({
    A: toBlocks(initialDeckA),
    B: toBlocks(initialDeckB),
  });
  const [loot, setLoot] = useState<CardData[]>(initialLoot);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const validA = useMemo(() => validateDeckComposition(fromBlocks(decks.A)), [decks.A]);
  const validB = useMemo(() => validateDeckComposition(fromBlocks(decks.B)), [decks.B]);
  const canSave = validA.valid && validB.valid && !saving;

  // Le carte si identificano per POSIZIONE, non per id: i due mazzi di una
  // run possono condividere la stessa istanza fisica (stesso id in A e in B),
  // e con gli id il drag/scambio colpiva la copia nel mazzo sbagliato.
  // Chiave slot: "A:hand2#1", "B:missions#0", "loot#3".
  const zoneKey = (z: ZoneRef) => (z.deck === 'loot' ? 'loot' : `${z.deck}:${z.block}`);
  const parseZone = (key: string): ZoneRef => {
    if (key === 'loot') return { deck: 'loot' };
    const [deck, block] = key.split(':') as [DeckKey, BlockKey];
    return { deck, block };
  };
  const parseSlot = (slot: string): { zone: ZoneRef; index: number } => {
    const [key, idx] = slot.split('#');
    return { zone: parseZone(key), index: Number(idx) };
  };

  const listOf = (z: ZoneRef, d = decks, l = loot): CardData[] =>
    z.deck === 'loot' ? l : d[z.deck][z.block];
  const cardAt = (slot: string): CardData | undefined => {
    const { zone, index } = parseSlot(slot);
    return listOf(zone)[index];
  };

  // Una carta può stare in un blocco solo se del tipo giusto (Mission ↔
  // blocco missioni) e se c'è posto.
  const fits = (card: CardData, z: ZoneRef): boolean => {
    if (z.deck === 'loot') return true;
    return (card.type === 'Mission') === (z.block === 'missions');
  };

  // Applica una nuova lista a una zona su copie di stato.
  const writeZone = (z: ZoneRef, list: CardData[], d: Record<DeckKey, DeckBlocks>, setL: (l: CardData[]) => void) => {
    if (z.deck === 'loot') setL(list);
    else d[z.deck] = { ...d[z.deck], [z.block]: list };
  };

  const moveCard = (slot: string, dest: ZoneRef) => {
    const { zone: src, index } = parseSlot(slot);
    if (zoneKey(src) === zoneKey(dest)) return;
    const card = listOf(src)[index];
    if (!card || !fits(card, dest)) return;
    if (dest.deck !== 'loot' && decks[dest.deck][dest.block].length >= BLOCK_MAX[dest.block]) return;
    const d = { ...decks };
    let newLoot = loot;
    const setL = (l: CardData[]) => { newLoot = l; };
    writeZone(src, listOf(src, d, newLoot).filter((_, i) => i !== index), d, setL);
    writeZone(dest, [...listOf(dest, d, newLoot), card], d, setL);
    setDecks(d);
    setLoot(newLoot);
  };

  // Scambia due carte di posto (stesso tipo), anche tra mazzi e loot.
  const swapCards = (slotA: string, slotB: string) => {
    const a = parseSlot(slotA);
    const b = parseSlot(slotB);
    const ca = listOf(a.zone)[a.index];
    const cb = listOf(b.zone)[b.index];
    if (!ca || !cb || !fits(ca, b.zone) || !fits(cb, a.zone)) return;
    const d = { ...decks };
    let newLoot = loot;
    const setL = (l: CardData[]) => { newLoot = l; };
    if (zoneKey(a.zone) === zoneKey(b.zone)) {
      const list = [...listOf(a.zone, d, newLoot)];
      list[a.index] = cb;
      list[b.index] = ca;
      writeZone(a.zone, list, d, setL);
    } else {
      writeZone(a.zone, listOf(a.zone, d, newLoot).map((c, i) => (i === a.index ? cb : c)), d, setL);
      writeZone(b.zone, listOf(b.zone, d, newLoot).map((c, i) => (i === b.index ? ca : c)), d, setL);
    }
    setDecks(d);
    setLoot(newLoot);
  };

  const suggest = (deck: DeckKey) => {
    const cards = fromBlocks(decks[deck]);
    if (!validateDeckComposition(cards).valid) return;
    setDecks({ ...decks, [deck]: toBlocks(suggestLayout(cards)) });
  };

  const handleDragStart = (e: DragStartEvent) => setActiveDragId(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveDragId(null);
    if (!e.over) return;
    const slot = String(e.active.id);
    const overId = String(e.over.id);
    if (overId.startsWith('card:')) {
      const other = overId.slice(5);
      if (other !== slot) swapCards(slot, other);
      return;
    }
    moveCard(slot, parseZone(overId));
  };

  // Click: dal mazzo al loot; dal loot al primo blocco con posto, dando la
  // precedenza al mazzo non valido (è lì che manca la carta).
  const handleClick = (slot: string) => {
    const { zone } = parseSlot(slot);
    if (zone.deck !== 'loot') { moveCard(slot, { deck: 'loot' }); return; }
    const card = cardAt(slot);
    if (!card) return;
    const order: DeckKey[] = !validA.valid || validB.valid ? ['A', 'B'] : ['B', 'A'];
    for (const deck of order) {
      const blocks = card.type === 'Mission' ? (['missions'] as BlockKey[]) : HAND_BLOCKS;
      const block = blocks.find(b => decks[deck][b].length < BLOCK_MAX[b]);
      if (block) { moveCard(slot, { deck, block }); return; }
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setServerError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/reconfigure-decks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // L'ordine delle carte è la disposizione a blocchi.
        body: JSON.stringify({ A: fromBlocks(decks.A), B: fromBlocks(decks.B) }),
      });
      const json = await res.json();
      if (!res.ok) {
        setServerError(json.error ?? 'save failed');
        return;
      }
      onSaved(json.run);
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'network error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const activeCard = activeDragId ? cardAt(activeDragId) : null;

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ color: '#ffb703', margin: 0 }}>Reconfigure Decks</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ color: '#aaa', fontSize: 12, marginTop: 0, marginBottom: 16 }}>
          Drag cards between decks and loot, or drop a card onto another to swap them.
          Each deck needs 14 cards: 2 suits, Hand 1-6 + 1 Mission per suit, arranged as Table / Hand 1 / Hand 2 (4 cards each).
        </p>

        {serverError && (
          <div style={{ color: '#e57373', background: '#2a1010', padding: '6px 12px', borderRadius: 4, marginBottom: 12, fontSize: 12 }}>
            {serverError}
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div style={{ display: 'flex', gap: 16 }}>
            {(['A', 'B'] as const).map(deck => {
              const v = deck === 'A' ? validA : validB;
              return (
                <div key={deck} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--accent-color)', fontWeight: 'bold', fontSize: 13 }}>Deck {deck}</span>
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {v.valid
                        ? <span style={{ color: '#4ade80', fontSize: 11 }}>✓ valid</span>
                        : <span style={{ color: '#fbbf24', fontSize: 11 }} title={v.reason}>✗ {fromBlocks(decks[deck]).length}/14</span>}
                      <button
                        onClick={() => suggest(deck)}
                        disabled={!v.valid}
                        title="Rearrange with the suggested layout"
                        style={{ ...btnSmall, opacity: v.valid ? 1 : 0.4, cursor: v.valid ? 'pointer' : 'not-allowed' }}
                      >Suggest</button>
                    </span>
                  </div>
                  {ALL_BLOCKS.map(block => (
                    <Zone
                      key={block}
                      id={zoneKey({ deck, block })}
                      label={`${BLOCK_LABEL[block]} (${decks[deck][block].length}/${BLOCK_MAX[block]})`}
                      cards={decks[deck][block]}
                      onCardClick={handleClick}
                    />
                  ))}
                </div>
              );
            })}
            <div style={{ flex: 1, maxHeight: '60vh', overflowY: 'auto' }}>
              <Zone id="loot" label={`Loot (${loot.length})`} cards={loot} onCardClick={handleClick} tall />
            </div>
          </div>
          <DragOverlay>
            {activeCard ? (
              <div style={{ transform: 'scale(1.05)', filter: 'drop-shadow(0 0 10px var(--accent-color))' }}>
                <Card card={activeCard} staticContext />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} disabled={saving} style={btnNeutral}>Cancel</button>
          <button onClick={handleSave} disabled={!canSave} style={canSave ? btnPrimary : { ...btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}>
            {saving ? 'Saving...' : 'Save new configuration'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ZoneProps {
  id: string;
  label: string;
  cards: CardData[];
  onCardClick: (slot: string) => void;
  tall?: boolean;
}

function Zone({ id, label, cards, onCardClick, tall }: ZoneProps) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        background: isOver ? '#22222c' : '#16161e',
        border: `2px ${isOver ? 'solid var(--accent-color)' : 'dashed #444'}`,
        borderRadius: 8,
        padding: 8,
        minHeight: tall ? 360 : 90,
        flexShrink: 0, // cresce con le carte; è la colonna a scrollare
      }}
    >
      <div style={{ color: '#bbb', fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {cards.length === 0 ? (
          <div style={{ color: '#666', fontStyle: 'italic', fontSize: 12, padding: '12px 8px' }}>empty</div>
        ) : (
          cards.map((c, i) => {
            const slot = `${id}#${i}`;
            return <DraggableCard key={slot} slot={slot} card={c} onClick={() => onCardClick(slot)} />;
          })
        )}
      </div>
    </div>
  );
}

function DraggableCard({ slot, card, onClick }: { slot: string; card: CardData; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: slot });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `card:${slot}` });
  return (
    <div
      ref={(el) => { setNodeRef(el); setDropRef(el); }}
      {...attributes}
      {...listeners}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        opacity: isDragging ? 0.3 : 1,
        outline: isOver ? '2px solid var(--accent-color)' : 'none',
        outlineOffset: 2,
        borderRadius: 8,
      }}
      title="Click to move · drag onto a block, or onto another card to swap"
    >
      <Card card={card} staticContext />
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.8)',
  zIndex: 1100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const panelStyle: React.CSSProperties = {
  background: '#0e0e16',
  border: '2px solid #ffb703',
  borderRadius: 10,
  padding: 20,
  maxWidth: 1300,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 0 25px rgba(255, 183, 3, 0.3)',
};

const btnPrimary: React.CSSProperties = {
  background: '#ffb703',
  border: 'none',
  color: '#000',
  padding: '10px 22px',
  borderRadius: 6,
  fontWeight: 'bold',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnNeutral: React.CSSProperties = {
  background: '#333',
  border: 'none',
  color: '#bbb',
  padding: '10px 22px',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnSmall: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #ffb703',
  color: '#ffb703',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontFamily: 'inherit',
};
